import { useCallback, useEffect, useRef, useState } from 'react';

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'wheel', 'scroll', 'input'] as const;

const TICK_MS = 1000;

interface UseIdleTimeoutOptions {
  enabled?: boolean;
  timeoutMs: number;
  warningMs: number;
  onTimeout: () => void;
}

interface UseIdleTimeoutResult {
  secondsRemaining: number | null;
  extend: () => void;
}

export const useIdleTimeout = ({
  enabled = true,
  timeoutMs,
  warningMs,
  onTimeout,
}: UseIdleTimeoutOptions): UseIdleTimeoutResult => {
  const [secondsRemaining, setSecondsRemaining] = useState<number | null>(null);

  const deadlineRef = useRef(0);
  const onTimeoutRef = useRef(onTimeout);

  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  const extend = useCallback(() => {
    deadlineRef.current = Date.now() + timeoutMs;
    setSecondsRemaining(null);
  }, [timeoutMs]);

  useEffect(() => {
    if (!enabled) return;

    extend();

    const checkDeadline = (now = Date.now()) => {
      const remainingMs = deadlineRef.current - now;

      if (remainingMs <= 0) {
        // Reset first so the hook remains stable if the component stays mounted.
        extend();
        onTimeoutRef.current();
        return;
      }

      if (remainingMs <= warningMs) {
        setSecondsRemaining(Math.ceil(remainingMs / 1000));
      }
    };

    const handleActivity = () => {
      const now = Date.now();
      const remainingMs = deadlineRef.current - now;

      if (remainingMs > warningMs) {
        deadlineRef.current = now + timeoutMs;
      } else {
        // Activity cannot extend the session during warning or after expiry,
        // but immediately refresh the countdown/timeout state.
        checkDeadline(now);
      }
    };

    ACTIVITY_EVENTS.forEach((eventName) => {
      document.addEventListener(eventName, handleActivity, {
        capture: true,
        passive: true,
      });
    });

    checkDeadline();
    const intervalId = window.setInterval(checkDeadline, TICK_MS);

    return () => {
      window.clearInterval(intervalId);

      ACTIVITY_EVENTS.forEach((eventName) => {
        document.removeEventListener(eventName, handleActivity, {
          capture: true,
        });
      });
    };
  }, [enabled, timeoutMs, warningMs, extend]);

  return {
    secondsRemaining: enabled ? secondsRemaining : null,
    extend,
  };
};
